import { Plus, Signpost, Ticket, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import type { LeisureHint } from '@shh/shared';
import { Button, Heading, Icon, Surface, Text, cardSurface, cn } from '@shh/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useLens } from '../../app/lens';
import { leisureHintFor } from '../../data/hints';
import { useFamily } from '../../data/family';
import { useWeather } from '../../data/weather';
import { funText } from '../../i18n/fun';

/** Who may do what in Freizeit – the same answers the rules give. */
export function useLeisureRights() {
  const f = useFamily();
  const lens = useLens();
  const canPlan = lens.canParent || f.role === 'teen';
  return {
    canPlan,
    /** whose heart a tap gives: the signed-in person, on the wall the person picked at the top */
    me: f.meId ?? lens.member?.id,
    /** adults see "Ich besorge das" (parents, the wall with the PIN) */
    adult: lens.canParent,
    /** may change this person's wish list */
    editsWishes: (owner: string) =>
      owner === 'family' || lens.canParent || f.role === 'device' || f.meId === owner,
  };
}

/** The weekend suggestion (or a voucher reminder) – the same one "Heute" shows. */
export function useLeisureHint(): LeisureHint | null {
  const f = useFamily();
  return leisureHintFor(f, useWeather(f.household?.location, f.timeZone));
}

export function SuggestionCard({ hint }: { hint: LeisureHint }) {
  return (
    <Surface tone="sunken" className="flex items-start gap-gap-md">
      <Icon
        icon={hint.kind === 'voucher' ? Ticket : Signpost}
        size="lg"
        weight="duotone"
        className="shrink-0 text-accent-primary"
      />
      <div className="flex min-w-0 flex-col gap-gap-xs">
        <span className="text-sm font-bold text-text-secondary">{funText.goodToKnow}</span>
        <Link to={hint.href} className="font-semibold underline underline-offset-2">
          {hint.text}
        </Link>
      </div>
    </Surface>
  );
}

/** Section head inside a Freizeit page: title, optional count link and "＋" action. */
export function SectionHead({
  id,
  title,
  link,
  onAdd,
  addLabel,
}: {
  id: string;
  title: string;
  link?: { to: string; label: string };
  onAdd?: () => void;
  addLabel?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-gap-sm">
      <Heading level={2} size="md" id={id}>
        {title}
      </Heading>
      <div className="flex items-center gap-gap-sm">
        {link && (
          <Link to={link.to} className="text-sm font-bold underline underline-offset-2">
            {link.label}
          </Link>
        )}
        {onAdd && (
          <Button variant="secondary" icon={Plus} onPress={onAdd}>
            {addLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Nothing there yet – an invitation, never an error. */
export function Empty({ icon, children }: { icon: PhosphorIcon; children: ReactNode }) {
  return (
    <Surface tone="sunken" className="flex items-start gap-gap-md">
      <Icon icon={icon} size="lg" className="shrink-0 text-text-muted" />
      <Text tone="secondary">{children}</Text>
    </Surface>
  );
}

/** A card that is a link (router links have no React Aria press state – focus ring by hand). */
export const cardLink = cn(
  cardSurface,
  'rounded-card outline-none hover:border-border-default focus-visible:outline-3 focus-visible:outline-border-focus',
);
